// Keeps friends' contact details fresh (brief §2). The bug it fixes: an
// installed PWA that only synced on cold start and over a single WebSocket never
// picked up a friend's edits once that socket had quietly died (backgrounded
// tab, network change, sleep/wake). So we sync on every meaningful "the app is
// back in front of the user" signal — foreground, focus, regained network — and
// on a slow heartbeat while visible, and we surface the state so the UI can show
// "Synchronisation…".
//
// Live WebSocket delivery stays as a best-effort fast path on top; the polling
// is what makes eventual delivery guaranteed.

import { relay } from './config';
import { syncInbox, startRealtime, publishMyProfile, restoreMyProfileFromRelay } from './sync';
import { isPublishPending } from './publishQueue';
import { setSyncState } from './syncState';
import type { StoredIdentity } from './identity';

// Heartbeat while the app is visible. Long enough to be gentle on battery/data,
// short enough that a friend's edit shows up on its own within a minute or two.
const POLL_MS = 45_000;

// The active manager's sync trigger, so on-demand callers (pull-to-refresh) can
// force a sync and await the very same in-flight run. Null when no manager runs.
let activeSync: (() => Promise<void>) | null = null;

/**
 * Force a sync now and resolve when it finishes (sharing the in-flight run if
 * one is already going). Used by pull-to-refresh. No-op resolve when no manager
 * is running yet.
 */
export function requestSync(): Promise<void> {
  return activeSync ? activeSync() : Promise.resolve();
}

/**
 * Start the background sync loop. `onUpdate` fires whenever a sync actually
 * changed a cached profile, so the caller can re-render the affected screens.
 * Returns a disposer that tears down every timer, listener and socket.
 */
export function startSyncManager(id: StoredIdentity, onUpdate: () => void): () => void {
  let disposed = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let realtimeStop: (() => void) | null = null;
  // Concurrent callers (heartbeat, focus, pull-to-refresh) share one run rather
  // than stacking requests, and each awaits its real completion.
  let inFlight: Promise<void> | null = null;

  function runSync(): Promise<void> {
    if (disposed) return Promise.resolve();
    if (inFlight) return inFlight;
    if (!relay.configured) {
      setSyncState({ status: 'disabled' });
      return Promise.resolve();
    }
    // Surface a queued edit even while we can't reach the relay yet, so the
    // indicator reflects it immediately rather than only after the next
    // successful attempt.
    void isPublishPending().then((pending) => setSyncState({ pendingPublish: pending }));
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setSyncState({ status: 'offline' });
      return Promise.resolve();
    }
    setSyncState({ status: 'syncing' });
    inFlight = (async () => {
      try {
        // Pull my own state first: an edit made on another of my devices has to
        // land before friends' envelopes are processed, since an envelope from
        // someone missing from my Cercle is dropped on purpose.
        const selfChanged = await restoreMyProfileFromRelay(id);
        const { updated } = await syncInbox(id);
        // Retry a profile edit that couldn't reach friends earlier — this is
        // what makes an offline save eventually land without the user having
        // to re-save. Failure here just leaves it queued for the next trigger.
        if (await isPublishPending()) {
          try {
            await publishMyProfile(id);
          } catch {
            /* still unreachable — stays queued */
          }
        }
        setSyncState({ status: 'idle', lastSyncAt: Date.now() });
        if (updated > 0 || selfChanged) onUpdate();
      } catch {
        setSyncState({ status: 'error' });
      }
    })().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  activeSync = runSync;

  async function connectRealtime(): Promise<void> {
    if (disposed || realtimeStop || !relay.configured) return;
    try {
      realtimeStop = await startRealtime(id, () => onUpdate());
    } catch {
      realtimeStop = null; // polling still guarantees delivery
    }
  }

  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      void runSync();
      void connectRealtime();
    }
  };
  const onOnline = (): void => {
    void runSync();
    void connectRealtime();
  };
  const onFocus = (): void => void runSync();

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);
  window.addEventListener('focus', onFocus);

  // Heartbeat only fires work while the app is actually on screen.
  timer = setInterval(() => {
    if (document.visibilityState === 'visible') void runSync();
  }, POLL_MS);

  void runSync();
  void connectRealtime();

  return () => {
    disposed = true;
    if (activeSync === runSync) activeSync = null;
    if (timer !== null) clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('focus', onFocus);
    realtimeStop?.();
  };
}
